import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Spinner,
  Text,
  makeStyles,
} from "@fluentui/react-components";
import { FilteredComboBox } from "../shared/FilteredCombobox.js";

const useStyles = makeStyles({
  card: {
    border: "1px solid var(--colorNeutralStroke2)",
    borderRadius: "8px",
    padding: "12px",
    display: "grid",
    gap: "8px",
  },
  errorText: {
    color: "var(--colorPaletteRedForeground1)",
  },
  rootEntityRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  rootEntityCombo: {
    minWidth: "320px",
    flex: 1,
  },
});

export interface EntitySelectionSectionProps {
  normalizedBaseUrl: string;
  entitiesLoading: boolean;
  entitiesError: string;
  rootEntityOptions: Array<[string, string]>;
  rootEntityLogicalName: string;
  onSelectRootEntity: (value: string) => void;
  onClearRootEntity: () => void;
  rootMetadataLoading: boolean;
  rootMetadataError: string;
}

export function EntitySelectionSection({
  normalizedBaseUrl,
  entitiesLoading,
  entitiesError,
  rootEntityOptions,
  rootEntityLogicalName,
  onSelectRootEntity,
  onClearRootEntity,
  rootMetadataLoading,
  rootMetadataError,
}: EntitySelectionSectionProps): JSX.Element {
  const styles = useStyles();
  const [confirmClearOpen, setConfirmClearOpen] = React.useState(false);
  const hasSelectedRootEntity = rootEntityLogicalName.trim().length > 0;

  return (
    <div className={styles.card}>
      <Text weight="semibold">FetchXML Builder</Text>
      <Text>Connected to: {normalizedBaseUrl || "(not connected)"}</Text>

      {entitiesLoading ? <Spinner label="Loading entities" /> : null}
      {entitiesError ? <Text className={styles.errorText}>{entitiesError}</Text> : null}

      <Field label="Root entity">
        <div className={styles.rootEntityRow}>
          <div className={styles.rootEntityCombo}>
            <FilteredComboBox
              options={rootEntityOptions}
              selectedOption={rootEntityLogicalName}
              onValueChange={onSelectRootEntity}
              disabled={hasSelectedRootEntity}
            />
          </div>
          <Button appearance="secondary" disabled={!hasSelectedRootEntity} onClick={() => setConfirmClearOpen(true)}>
            Clear
          </Button>
        </div>
      </Field>

      <Dialog open={confirmClearOpen} onOpenChange={(_event, data) => setConfirmClearOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirm entity change</DialogTitle>
            <DialogContent>
              Are you sure you want to select a different entity?<br/>This action will discard the current query
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => {
                  setConfirmClearOpen(false);
                  onClearRootEntity();
                }}
              >
                Yes
              </Button>
              <Button appearance="secondary" onClick={() => setConfirmClearOpen(false)}>
                No
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {rootMetadataLoading ? <Spinner label="Loading root metadata" /> : null}
      {rootMetadataError ? <Text className={styles.errorText}>{rootMetadataError}</Text> : null}
    </div>
  );
}
